/**
 * anchored-panel.ts — pozycjonowanie portalowanego panelu (rozwijana lista) względem triggera.
 *
 * Nasze trzy ręcznie pisane listy (`MultiSelect`, `RecordSelect`, `SearchableSelect`) portalują
 * panel do `body` z `position: fixed` (§4.12 — inaczej `absolute` przycina kontener ze scrollem),
 * ale wszystkie kładły go BEZWARUNKOWO POD triggerem i z twardym `max-h-64`. Efekt: pole na
 * dole formularza rozwijało listę za dolną krawędź okna i dół był nieosiągalny.
 *
 * Ten helper liczy pozycję Z ODBICIEM: gdy pod triggerem nie ma miejsca, panel idzie NAD niego;
 * wysokość jest zawsze docięta do realnie dostępnego pasma, więc panel nigdy nie wychodzi za
 * ekran (w środku ma własny scroll). Trzymany osobno, bo trzy kopie tej samej arytmetyki
 * rozjechałyby się przy pierwszej poprawce — i dlatego, że logikę „czy odbić" da się
 * przetestować bez montowania komponentu.
 */

export type PanelPlacement = 'below' | 'above';

export interface AnchoredPanelPosition {
  top: number;
  left: number;
  width: number;
  /** Docięta wysokość panelu — konsument nakłada ją jako `maxHeight` (panel scrolluje w środku). */
  maxHeight: number;
  placement: PanelPlacement;
  /**
   * TYLKO dla `placement: 'above'` — odległość od DOLNEJ krawędzi okna do miejsca tuż nad triggerem.
   * Konsument, który chce, by odbity panel siedział przyklejony do triggera niezależnie od tego, jak
   * krótka jest jego treść, nakłada to jako `bottom` i NIE ustawia `top` (panel rośnie w górę od
   * triggera, docięty do `maxHeight`). Pole opcjonalne — konsumenci czytający tylko `top` kotwiczą
   * górną krawędź jak dotąd (krótki panel odrywa się w górę, ale to zachowanie kompatybilne wstecz).
   */
  anchorBottom?: number;
}

export interface AnchoredPanelOptions {
  /** Wysokość okna (wstrzykiwana dla testów; domyślnie `window.innerHeight`). */
  viewportHeight?: number;
  /** Szerokość okna (wstrzykiwana dla testów; domyślnie `window.innerWidth`). */
  viewportWidth?: number;
  /**
   * Minimalna szerokość panelu. Panel jest szeroki jak trigger, ale NIE węższy niż to minimum —
   * trigger w wąskiej kolumnie tabeli (filtr „Firma" ~80px) dawał panel, w którym nic nie było
   * widać (etykiety łamane po literze, poziomy scroll). Szerszy od triggera panel jest docinany
   * do okna i przesuwany w lewo, gdy wyjechałby za prawą krawędź.
   */
  minWidth?: number;
  /** Preferowana wysokość panelu — tyle, ile miał dawny `max-h-64` (16rem). */
  preferredMaxHeight?: number;
  /** Odstęp panelu od triggera. */
  gap?: number;
  /** Minimalny margines od krawędzi okna, żeby panel nie kleił się do brzegu. */
  viewportMargin?: number;
  /**
   * Minimalna sensowna wysokość panelu. Poniżej tej wartości „miejsce pod triggerem" nie jest
   * miejscem — lepiej odbić w górę, nawet jeśli tam też jest ciasno (widać wtedy więcej opcji).
   */
  minUsableHeight?: number;
}

/**
 * Pozycja panelu dla prostokąta triggera (`getBoundingClientRect()`).
 *
 * Reguła wyboru strony: domyślnie POD triggerem. Odbijamy NAD, gdy pod triggerem jest mniej
 * miejsca niż `minUsableHeight` ORAZ nad triggerem jest go więcej — czyli odbicie musi realnie
 * poprawić sytuację. Bez drugiego warunku panel skakałby w górę także wtedy, gdy i tam jest
 * ciasno (np. pole w niskim oknie), co tylko myli użytkownika.
 */
export function computeAnchoredPanelPosition(
  rect: { top: number; bottom: number; left: number; width: number },
  options: AnchoredPanelOptions = {},
): AnchoredPanelPosition {
  const viewportHeight =
    options.viewportHeight ?? (typeof window === 'undefined' ? 0 : window.innerHeight);
  const preferredMaxHeight = options.preferredMaxHeight ?? 256;
  const gap = options.gap ?? 4;
  const viewportMargin = options.viewportMargin ?? 8;
  const minUsableHeight = options.minUsableHeight ?? 160;
  const viewportWidth =
    options.viewportWidth ?? (typeof window === 'undefined' ? 0 : window.innerWidth);
  const minWidth = options.minWidth ?? 240;

  // Poziomo: szerokość = max(trigger, minimum), docięta do okna; lewa krawędź przy triggerze,
  // ale przesunięta w lewo, żeby prawa krawędź nie wyszła za okno. Bez znanej szerokości okna
  // (SSR/testy bez `viewportWidth`) — zachowanie jak dawniej (tylko minimum szerokości).
  const width =
    viewportWidth > 0
      ? Math.min(Math.max(rect.width, minWidth), viewportWidth - 2 * viewportMargin)
      : Math.max(rect.width, minWidth);
  const left =
    viewportWidth > 0
      ? Math.max(viewportMargin, Math.min(rect.left, viewportWidth - viewportMargin - width))
      : rect.left;

  const spaceBelow = viewportHeight - rect.bottom - gap - viewportMargin;
  const spaceAbove = rect.top - gap - viewportMargin;

  const flipUp = spaceBelow < minUsableHeight && spaceAbove > spaceBelow;

  if (flipUp) {
    const maxHeight = Math.max(0, Math.min(preferredMaxHeight, spaceAbove));
    return {
      // Wsteczna kompatybilność: konsumenci czytający tylko `top` dalej kotwiczą górną krawędź.
      top: Math.max(viewportMargin, rect.top - gap - maxHeight),
      left,
      width,
      maxHeight,
      placement: 'above',
      // Preferowana kotwica dla odbicia: DOLNA krawędź panelu tuż nad triggerem. Dzięki temu krótki
      // panel siedzi przy triggerze zamiast unosić się w górę (rośnie ku górze, docięty do maxHeight).
      anchorBottom: viewportHeight - (rect.top - gap),
    };
  }

  return {
    top: rect.bottom + gap,
    left,
    width,
    maxHeight: Math.max(0, Math.min(preferredMaxHeight, spaceBelow)),
    placement: 'below',
  };
}
